import { bridgePolygonPieces } from "../bridgePolygonPieces";
import { closePolygonGaps } from "../closePolygonGaps";
import { unionPolygons } from "../unionPolygons";
import type { SelectionAreaPoint } from "../../types";
import type { MultiPolygon, PolygonRing } from "../types";
import type { MergeRoomPolygons } from "./types";

const MIN_RING_POINTS = 3;
// Gaps up to twice a wall are filled whole; wider ones get a wall-wide bridge.
const CLOSING_CAP_FACTOR = 2;
// Halvings of the closing radius; enough to land within a tenth of a point.
const CLOSING_SEARCH_STEPS = 10;
const COORDINATE_DECIMALS = 100;

const dropRepeatedPoints = (ring: PolygonRing): PolygonRing =>
  ring.filter((point, index) => {
    const previous = ring[(index + ring.length - 1) % ring.length];
    return point.x !== previous.x || point.y !== previous.y;
  });

const roundPoint = (point: SelectionAreaPoint): SelectionAreaPoint => ({
  x: Math.round(point.x * COORDINATE_DECIMALS) / COORDINATE_DECIMALS,
  y: Math.round(point.y * COORDINATE_DECIMALS) / COORDINATE_DECIMALS,
});

// The smallest closing radius up to the cap that leaves one piece; the pieces unchanged when none does.
// NOTE: a fixed one-wall radius would fill walls consistently, but also notches narrower than two walls; left as is for now.
const closeWithSmallestRadius = (
  pieces: MultiPolygon,
  cap: number,
): MultiPolygon => {
  let closed = closePolygonGaps({ pieces, radius: cap });
  if (closed.length !== 1) return pieces;
  let low = 0;
  let high = cap;
  for (let step = 0; step < CLOSING_SEARCH_STEPS; step++) {
    const middle = (low + high) / 2;
    const candidate = closePolygonGaps({ pieces, radius: middle });
    if (candidate.length === 1) {
      high = middle;
      closed = candidate;
    } else {
      low = middle;
    }
  }
  return closed;
};

// Union, then fill wall gaps, then bridge what is still apart: one polygon that keeps every room whole.
export const mergeRoomPolygons: MergeRoomPolygons = ({
  rings,
  wallWidthPt,
}) => {
  const cleanRings = rings
    .map(dropRepeatedPoints)
    .filter((ring) => ring.length >= MIN_RING_POINTS);
  if (cleanRings.length === 0) return [];

  try {
    let pieces = unionPolygons({ pieces: cleanRings.map((ring) => [ring]) });
    if (pieces.length > 1) {
      pieces = closeWithSmallestRadius(
        pieces,
        wallWidthPt * CLOSING_CAP_FACTOR,
      );
    }
    if (pieces.length > 1) {
      pieces = bridgePolygonPieces({ pieces, width: wallWidthPt });
    }
    if (pieces.length !== 1) return [];
    // Rounding can land neighbouring points on the same spot; those would be zero-length edges.
    return dropRepeatedPoints(pieces[0][0].map(roundPoint));
  } catch {
    return [];
  }
};
